import React from 'react';
import { Building, Users, Rocket, ChevronDown } from 'lucide-react';
import { useClient } from '../contexts/ClientContext';

const ModeSelector: React.FC = () => {
  const {
    clients,
    selectedClientId,
    selectedClient,
    setSelectedClientId,
    isLoading,
    mode,
    setMode,
    leadgenProperties,
    selectedPropertyId,
    setSelectedPropertyId
  } = useClient();

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'internal':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'active':
        return 'status-badge status-active';
      case 'pending':
        return 'status-badge status-warning';
      default:
        return 'status-badge status-paused';
    }
  };

  const buttonBase = 'px-2 py-1 text-xs font-medium transition-colors flex items-center gap-1.5';
  const buttonActive = 'bg-primary-500 text-black';
  const buttonInactive = 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700';

  return (
    <div className="flex items-center gap-2">
      {/* Mode Toggle - Three buttons */}
      <div className="flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
        <button
          onClick={() => setMode('internal')}
          className={`${buttonBase} ${mode === 'internal' ? buttonActive : buttonInactive}`}
        >
          <Building className="w-3 h-3" />
          Internal
        </button>
        <button
          onClick={() => setMode('client')}
          className={`${buttonBase} border-l border-gray-300 dark:border-gray-600 ${mode === 'client' ? buttonActive : buttonInactive}`}
        >
          <Users className="w-3 h-3" />
          Client
        </button>
        <button
          onClick={() => setMode('leadgen')}
          className={`${buttonBase} border-l border-gray-300 dark:border-gray-600 ${mode === 'leadgen' ? buttonActive : buttonInactive}`}
        >
          <Rocket className="w-3 h-3" />
          Lead Gen
        </button>
      </div>

      {/* Client Dropdown - Compact */}
      {mode === 'client' && (
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <select
              value={selectedClientId || 'all'}
              onChange={(e) => setSelectedClientId(e.target.value === 'all' ? null : e.target.value)}
              disabled={isLoading}
              className="select pr-6 min-w-[140px]"
            >
              <option value="all">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.company}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
          </div>
          {selectedClient && (
            <span className={`px-1.5 py-0.5 text-[10px] rounded ${getStatusBadgeClass(selectedClient.status)}`}>
              {selectedClient.status}
            </span>
          )}
        </div>
      )}

      {/* Internal mode indicator */}
      {mode === 'internal' && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Operations
        </span>
      )}

      {/* Lead Gen property dropdown */}
      {mode === 'leadgen' && (
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <select
              value={selectedPropertyId || 'all'}
              onChange={(e) => setSelectedPropertyId(e.target.value === 'all' ? null : e.target.value)}
              className="select pr-6 min-w-[140px]"
            >
              <option value="all">All Properties</option>
              {leadgenProperties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.vertical})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeSelector;
